import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ColDef, GridReadyEvent } from "ag-grid-community";
import { useReceiptBanking, useReceiptBankingStore } from "hooks";
import { ReceiptBankingQuery, ReceiptBankingRow } from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import {
  AgGridTable,
  ConfirmDialog,
  LoadingState,
  DateCellEditor,
} from "components";
import { normalizeDateInput } from "components/ui-common/AgGridTable/DateCellEditor";
import type { QueryState } from "components/ui-common/AgGridTable/AgGridTable";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "utils";

const formatDate = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString("vi-VN");
};

const compareDate = (filterDate: Date, cellValue?: string) => {
  if (!cellValue) return -1;
  const cellDate = new Date(cellValue);
  if (Number.isNaN(cellDate.getTime())) return 1;
  const cellOnly = new Date(
    cellDate.getFullYear(),
    cellDate.getMonth(),
    cellDate.getDate(),
  );
  if (cellOnly < filterDate) return -1;
  if (cellOnly > filterDate) return 1;
  return 0;
};

const ReceiptBankings: React.FC = () => {
  const navigate = useNavigate();
  const gridApiRef = useRef<GridReadyEvent["api"] | null>(null);
  const [queryParams, setQueryParams] = useState<ReceiptBankingQuery>({
    page: 1,
    pageSize: 20,
  });
  const [confirmRow, setConfirmRow] = useState<ReceiptBankingRow | null>(null);
  const {
    rows,
    setRows,
    removeRow,
    editingId,
    setEditingId,
    isLoading,
    setLoading,
  } = useReceiptBankingStore();

  const { fetchList, deleteReceiptBanking } = useReceiptBanking();

  const { data: receiptBankingData, isFetching } = fetchList(queryParams, true);

  const withItemFields = useCallback(
    (receipt: ReceiptBankingRow): ReceiptBankingRow => {
      const firstItem = receipt.items?.[0];
      return {
        ...receipt,
        amount:
          receipt.amount ??
          (typeof firstItem?.amount === "number"
            ? firstItem.amount
            : firstItem?.amount
              ? Number(firstItem.amount)
              : undefined),
        partner_id: receipt.partner_id ?? firstItem?.partner_id,
        reason: receipt.reason ?? firstItem?.reason,
        business_field: receipt.business_field ?? firstItem?.business_field,
        item_description: receipt.item_description ?? firstItem?.description,
        vat_rate:
          receipt.vat_rate ??
          (typeof firstItem?.vat_rate === "number"
            ? firstItem.vat_rate
            : firstItem?.vat_rate
              ? Number(firstItem.vat_rate)
              : undefined),
        vat_amount:
          receipt.vat_amount ??
          (typeof firstItem?.vat_amount === "number"
            ? firstItem.vat_amount
            : firstItem?.vat_amount
              ? Number(firstItem.vat_amount)
              : undefined),
        pit_rate:
          receipt.pit_rate ??
          (typeof firstItem?.pit_rate === "number"
            ? firstItem.pit_rate
            : firstItem?.pit_rate
              ? Number(firstItem.pit_rate)
              : undefined),
        pit_amount:
          receipt.pit_amount ??
          (typeof firstItem?.pit_amount === "number"
            ? firstItem.pit_amount
            : firstItem?.pit_amount
              ? Number(firstItem.pit_amount)
              : undefined),
        net_amount:
          receipt.net_amount ??
          (typeof firstItem?.net_amount === "number"
            ? firstItem.net_amount
            : firstItem?.net_amount
              ? Number(firstItem.net_amount)
              : undefined),
      };
    },
    [],
  );

  useEffect(() => {
    if (receiptBankingData?.data) {
      const mapped =
        (receiptBankingData.data as ReceiptBankingRow[])?.map((item) =>
          withItemFields(item),
        ) ?? [];
      setRows(mapped);
    }
  }, [receiptBankingData, setRows, isFetching, withItemFields]);

  useEffect(() => {
    setLoading(Boolean(isFetching || deleteReceiptBanking.isPending));
  }, [isFetching, deleteReceiptBanking.isPending, setLoading]);

  const columns = useMemo<ColDef<ReceiptBankingRow>[]>(() => {
    return [
      {
        headerName: "Ngày thu",
        field: "receipt_date",
        minWidth: 140,
        filter: "agDateColumnFilter",
        filterParams: { comparator: compareDate },
        valueFormatter: (params) => formatDate(params.value),
        cellEditor: DateCellEditor,
        valueParser: (params) => normalizeDateInput(params.newValue),
      },
      {
        headerName: "Số phiếu",
        field: "receipt_code",
        minWidth: 140,
        // editable: false,
      },
      {
        headerName: "Tài khoản NH",
        field: "bank_account_id",
        minWidth: 150,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Ngân hàng",
        field: "bank_name",
        minWidth: 180,
        flex: 1,
      },
      {
        headerName: "Người giao dịch",
        field: "transaction_person",
        minWidth: 180,
        flex: 1,
      },
      {
        headerName: "Người nộp",
        field: "payer_name",
        minWidth: 180,
        flex: 1,
      },
      {
        headerName: "Địa chỉ",
        field: "address",
        minWidth: 200,
        flex: 1,
      },
      {
        headerName: "Diễn giải",
        field: "description",
        minWidth: 200,
        flex: 1,
      },
      {
        headerName: "Kèm theo",
        field: "attachments",
        minWidth: 160,
      },
      {
        headerName: "Phiếu xuất",
        field: "export_invoice_id",
        minWidth: 140,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Số tiền",
        field: "amount",
        minWidth: 160,
        filter: "agNumberColumnFilter",
        valueFormatter: (params) =>
          params.value !== undefined && params.value !== null
            ? Number(params.value).toLocaleString("vi-VN")
            : "",
      },
      {
        headerName: "Mã đối tác",
        field: "partner_id",
        minWidth: 140,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Lý do thu",
        field: "reason",
        minWidth: 180,
        flex: 1,
      },
      {
        headerName: "Lĩnh vực KD",
        field: "business_field",
        minWidth: 160,
      },
      {
        headerName: "Mô tả chi tiết",
        field: "item_description",
        minWidth: 220,
        flex: 1,
      },
      {
        headerName: "VAT %",
        field: "vat_rate",
        minWidth: 120,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Tiền VAT",
        field: "vat_amount",
        minWidth: 140,
        editable: false,
        filter: "agNumberColumnFilter",
        valueFormatter: (params) =>
          params.value !== undefined && params.value !== null
            ? Number(params.value).toLocaleString("vi-VN")
            : "",
      },
      {
        headerName: "PIT %",
        field: "pit_rate",
        minWidth: 120,
        filter: "agNumberColumnFilter",
      },
      {
        headerName: "Tiền PIT",
        field: "pit_amount",
        minWidth: 140,
        editable: false,
        filter: "agNumberColumnFilter",
        valueFormatter: (params) =>
          params.value !== undefined && params.value !== null
            ? Number(params.value).toLocaleString("vi-VN")
            : "",
      },
      {
        headerName: "Net",
        field: "net_amount",
        minWidth: 140,
        editable: false,
        filter: "agNumberColumnFilter",
        valueFormatter: (params) =>
          params.value !== undefined && params.value !== null
            ? Number(params.value).toLocaleString("vi-VN")
            : "",
      },
    ];
  }, []);

  const handleAdd = useCallback(() => {
    navigate(ROUTES.ACCOUNTING.BANK_RECEIPT_CREATE);
  }, [navigate]);

  const handleEditRow = useCallback(
    (params) => {
      if (params?.data?.id) {
        navigate(`${ROUTES.ACCOUNTING.BANK_RECEIPT_DETAIL}/${params.data.id}`);
      }
    },
    [navigate],
  );

  const handleDeleteRow = useCallback((row: ReceiptBankingRow) => {
    setConfirmRow(row);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (!confirmRow) return;
    deleteReceiptBanking.mutate(confirmRow.id, {
      onSuccess: () => {
        removeRow(confirmRow.id);
        if (editingId === confirmRow.id) {
          setEditingId(null);
        }
        setConfirmRow(null);
      },
      onError: () => {
        SnackbarUtils.error("Xóa phiếu thu không thành công");
      },
    });
  }, [confirmRow, deleteReceiptBanking, editingId, removeRow, setEditingId]);

  const handleQueryChange = useCallback(
    (query: QueryState) => {
      setQueryParams((prev) => {
        const next = {
          ...prev,
          page: query.pagination?.page ?? prev.page,
          pageSize: query.pagination?.pageSize ?? prev.pageSize,
          sortModel: query.sortModel,
          filterModel: query.filterModel,
        };
        const unchanged =
          next.page === prev.page &&
          next.pageSize === prev.pageSize &&
          next.sortModel === prev.sortModel &&
          next.filterModel === prev.filterModel;
        return unchanged ? prev : next;
      });
    },
    [setQueryParams],
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      editable: (params) =>
        params.data?.id === editingId || params.node.rowPinned === "top",
      flex: 1,
      minWidth: 140,
    }),
    [editingId],
  );

  return (
    <div className="p-6 space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-gray-900">
          [Kế toán] Phiếu thu ngân hàng
        </h1>
        <p className="text-sm text-gray-600">
          Quản lý phiếu thu ngân hàng: tạo mới, chỉnh sửa nhanh trên bảng và xoá
          khi cần.
        </p>
      </div>

      <div className="relative bg-white border border-gray-200 shadow-sm rounded-lg p-4 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Danh sách phiếu thu ngân hàng
            </h2>
            <p className="text-xs text-gray-500">
              Sử dụng grid để lọc, sắp xếp và vào trang chi tiết để thêm/cập
              nhật.
            </p>
          </div>
          <button
            type="button"
            onClick={handleAdd}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Plus className="w-4 h-4 text-green-700" />
            Thêm phiếu thu NH
          </button>
        </div>
        <AgGridTable
          rows={rows}
          columns={columns}
          height={520}
          autoHeight={false}
          defaultColDef={defaultColDef}
          totalPages={receiptBankingData?.totalPage}
          onEdit={handleEditRow}
          onDelete={handleDeleteRow}
          getRowId={(params) => String(params.data.id)}
          onQueryChange={handleQueryChange}
          onGridReady={(event) => {
            gridApiRef.current = event.api;
          }}
        />
        {isLoading && (
          <LoadingState
            message="Đang đồng bộ dữ liệu"
            description="Hệ thống đang cập nhật danh sách phiếu thu ngân hàng, vui lòng chờ giây lát."
          />
        )}
      </div>
      <ConfirmDialog
        open={Boolean(confirmRow)}
        tone="danger"
        title="Xóa phiếu thu?"
        description={`Bạn chắc chắn muốn xóa phiếu thu "${
          confirmRow?.receipt_code ?? ""
        }"? Thao tác này không thể khôi phục.`}
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        loading={deleteReceiptBanking.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
};

export default ReceiptBankings;
